/**
 * Browser Access Guard Component
 * 
 * Controls access to the web client based on LaunchDarkly feature flags.
 * Redirects users not on the allowlist to download-app.html
 * 
 * @module components/auth/BrowserAccessGuard
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

interface BrowserAccessGuardProps {
  children: React.ReactNode;
}

/**
 * Guards the application based on access-config feature flag
 * 
 * Feature flag behavior:
 * - enabled=false: All users can access (feature disabled)
 * - enabled=true, showToAll=true: All authenticated users can access
 * - enabled=true, showToUserIds=[...]: Only users in the list can access
 * 
 * Users not meeting criteria are redirected to onboarding page
 */
export function BrowserAccessGuard({ children }: BrowserAccessGuardProps) {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const location = useLocation();
  const ldClient = useLDClient();
  const flags = useFeatureFlags();
  const [flagsReady, setFlagsReady] = useState(false);

  // Update LaunchDarkly user context - call identify immediately when client is ready (required by LaunchDarkly)
  useEffect(() => {
    if (!ldClient) return;

    // Always call identify as soon as client is available to satisfy LaunchDarkly requirement
    if (isAuthenticated && user?.id) {
      ldClient.identify({
        kind: 'user',
        key: user.id.toString(),
        email: user.email,
        anonymous: false,
      }).then(() => {
        // Wait for flags to re-evaluate with new user context
        setFlagsReady(true);
      });
    } else {
      // Call identify for anonymous users immediately
      ldClient.identify({
        kind: 'user',
        key: 'anonymous',
        anonymous: true,
      }).then(() => {
        setFlagsReady(true);
      });
    }
  }, [ldClient, isAuthenticated, user]); // Prioritize ldClient in dependencies

  // Check browser access and redirect if needed
  useEffect(() => {
    // Skip check while authentication is initializing or flags not ready
    if (authLoading || !flagsReady) {
      return;
    }

    // Allow access to auth pages and root path without checking (check FIRST before any LaunchDarkly logic)
    const authPaths = ['/auth', '/sign-in', '/forgot-password', '/reset-password'];
    const currentPath = location.pathname.toLowerCase();
    
    // Check if current path is an excluded auth path
    const isAuthPath = authPaths.some(path => {
      const normalizedPath = path.toLowerCase();
      return currentPath === normalizedPath || currentPath.startsWith(normalizedPath + '/');
    });
    
    if (isAuthPath) {
      return;
    }

    // Allow root path (so users can get to sign-in)
    if (location.pathname === '/') {
      return;
    }

    // Only check access for authenticated users - unauthenticated users should always be able to access auth pages
    // (This handles the case where user signs out and gets redirected to /auth)
    if (!isAuthenticated) {
      return;
    }

    const accessConfig = flags.accessConfig;

    // If LaunchDarkly is not configured or feature is disabled, allow access
    if (!accessConfig || !accessConfig.enabled) {
      return;
    }

    // Check if browser access should be allowed
    // Convert user.id (string) to number for comparison with showToUserIds (number[])
    const userIdNumber = user?.id ? parseInt(user.id, 10) : null;
    
    // Check for internal domains (case-insensitive)
    const userEmail = user?.email?.toLowerCase();
    const isInternalUser = userEmail && (userEmail.endsWith('@sequel.ae') || userEmail.endsWith('@lore.co'));

    const shouldAllowAccess = 
      accessConfig.showToAll || 
      isInternalUser ||
      (userIdNumber !== null && !isNaN(userIdNumber) && accessConfig.showToUserIds.includes(userIdNumber));

    // Redirect to onboarding/download page if access is not allowed
    if (!shouldAllowAccess) {
      const env = process.env.REACT_APP_ENV || 'nonprod';
      const isProd = env === 'production' || env === 'prod';
      
      // Redirect to the appropriate onboarding page
      const onboardingUrl = isProd 
        ? 'https://community.lorehealthcare.com/onboarding/'
        : 'https://community-test.lorehealthcare.com/onboarding/';
      
      window.location.href = onboardingUrl;
    }
  }, [authLoading, isAuthenticated, user, flags, location.pathname, location.search, flagsReady]);

  return <>{children}</>;
}

